/* -------------------------------------------------------------------------
   Objektarten der Mietpreis-Analyse und ihre Folgen.

   Die Frage steht am Anfang des Fragebogens, weil sie alles Weitere bestimmt:
   welche Felder erscheinen, ob überhaupt gerechnet werden kann und mit welchem
   Vorbehalt das Ergebnis zu lesen ist. Die Auswertung beruht auf Mieten für
   Wohnungen — je weiter ein Objekt davon entfernt ist, desto deutlicher muss
   das gesagt werden.
   ------------------------------------------------------------------------- */

import React from 'react';

const svg = {
  width: 40, height: 40, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.3,
  strokeLinecap: 'round', strokeLinejoin: 'round',
  'aria-hidden': true, focusable: 'false',
};

/* Alle Piktogramme teilen denselben Gebäudeumriss, damit der Unterschied genau
   dort sichtbar wird, wo er liegt. */
export function Piktogramm({ art }) {
  return (
    <svg {...svg}>
      {art === 'wohnen' && (
        <>
          <rect x="4.5" y="3.5" width="15" height="17" rx="0.5" />
          <rect x="7" y="6.5" width="3.5" height="3" />
          <rect x="13.5" y="6.5" width="3.5" height="3" />
          <rect x="7" y="11.5" width="3.5" height="3" />
          <rect x="13.5" y="11.5" width="3.5" height="3" />
          <path d="M10 20.5v-4h4v4" />
        </>
      )}
      {art === 'gewerbe' && (
        <>
          <rect x="4.5" y="3.5" width="15" height="17" rx="0.5" />
          <rect x="7" y="6" width="10" height="3" />
          <rect x="7" y="10.5" width="10" height="3" />
          <path d="M4.5 15.5h15" />
          <rect x="7" y="17" width="10" height="3.5" />
        </>
      )}
      {/* Eine Wohnung: eine hervorgehobene Einheit im Haus */}
      {art === 'wohnung' && (
        <>
          <rect x="4.5" y="3.5" width="15" height="17" rx="0.5" />
          <path d="M4.5 10h15M4.5 15h15M12 10V3.5M12 15v5.5" opacity="0.45" />
          <rect x="12" y="10" width="7.5" height="5" fill="currentColor" opacity="0.16" />
          <rect x="12" y="10" width="7.5" height="5" />
        </>
      )}
      {art === 'mfh' && (
        <>
          <rect x="4.5" y="3.5" width="15" height="17" rx="0.5" />
          <rect x="7" y="6" width="3.5" height="2.5" />
          <rect x="13.5" y="6" width="3.5" height="2.5" />
          <rect x="7" y="10" width="3.5" height="2.5" />
          <rect x="13.5" y="10" width="3.5" height="2.5" />
          <rect x="7" y="14" width="3.5" height="2.5" />
          <rect x="13.5" y="14" width="3.5" height="2.5" />
          <path d="M10.5 20.5v-2h3v2" />
        </>
      )}
      {/* Einfamilienhaus: Satteldach und Grundstück statt Geschossen */}
      {art === 'efh' && (
        <>
          <path d="M3.5 10.5 12 4l8.5 6.5" />
          <path d="M5.5 9.5v11h13v-11" />
          <rect x="8" y="12.5" width="3" height="2.5" />
          <path d="M13.5 20.5v-5h3v5" />
        </>
      )}
      {art === 'gewerbe_gebaeude' && (
        <>
          <rect x="4.5" y="3.5" width="15" height="17" rx="0.5" />
          <rect x="7" y="6" width="10" height="3" />
          <rect x="7" y="10.5" width="10" height="3" />
          <path d="M4.5 15.5h15" />
          <rect x="7" y="17" width="10" height="3.5" />
        </>
      )}
      {art === 'gewerbe_einheit' && (
        <>
          <rect x="4.5" y="3.5" width="15" height="17" rx="0.5" />
          <path d="M4.5 15.5h15" opacity="0.45" />
          <rect x="7" y="6" width="10" height="3" opacity="0.35" />
          <rect x="7" y="10.5" width="10" height="3" opacity="0.35" />
          <rect x="7" y="17" width="10" height="3.5" fill="currentColor" fillOpacity="0.16" />
          <rect x="7" y="17" width="10" height="3.5" />
        </>
      )}
    </svg>
  );
}

/** Erste Ebene: Wohnen oder Gewerbe. */
export const OBERARTEN = [
  { id: 'wohnen', titel: 'Wohnen' },
  { id: 'gewerbe', titel: 'Gewerbe' },
];

/** Zweite Ebene, abhängig von der ersten. */
export const UNTERARTEN = {
  wohnen: [
    { id: 'wohnung', titel: 'Einzelne Wohnung' },
    { id: 'mfh', titel: 'Mehrfamilienhaus' },
    { id: 'efh', titel: 'Einfamilienhaus' },
  ],
  gewerbe: [
    { id: 'gewerbe_gebaeude', titel: 'Ganzes Gebäude' },
    { id: 'gewerbe_einheit', titel: 'Einzelne Einheit' },
  ],
};

/** Nur beim Mehrfamilienhaus: wie viel Gewerbe steckt darin. */
export const GEWERBEANTEIL = [
  { id: 'kein', titel: 'Kein Gewerbe' },
  { id: 'klassisch', titel: 'Wohn- und Geschäftshaus' },
  { id: 'hoch', titel: 'Höherer Gewerbeanteil' },
];

/** Der Hinweis, der zur getroffenen Auswahl gehört. `ton` steuert die Farbe. */
export function hinweisZu(objektart, gewerbeanteil) {
  if (objektart === 'efh') {
    return {
      ton: 'warn',
      kopf: 'Einfamilienhäuser unterscheiden sich stark.',
      text: 'Für die Bewertung nutzen wir Daten zu Wohnungsmieten. Grundstück, Garten und Alleinnutzung schlagen sich im '
        + 'Mietpreis nieder und fehlen in diesem Vergleich. Rechnen Sie damit, dass die '
        + 'Spanne für Ihr Haus eher zu niedrig ausfällt und nur als Anhaltspunkt dient.'
    };
  }
  if (objektart === 'wohnung') {
    return {
      ton: 'info',
      kopf: 'Passt.',
      text: 'Für einzelne Wohnungen ist die Datengrundlage gemacht — Angebotsmieten für '
        + 'Wohnungen von 40 bis 120 m².',
    };
  }
  if (objektart === 'mfh') {
    if (gewerbeanteil === 'klassisch') {
      return {
        ton: 'info',
        kopf: 'Der Normalfall im Bestand.',
        text: 'Beim klassischen Wohn- und Geschäftshaus trägt der Wohnteil das Objekt. '
          + 'Wir werten ihn aus und erfassen die Gewerbeeinheit(en) getrennt und lassen sie '
          + 'aus der Rechnung heraus.',
      };
    }
    if (gewerbeanteil === 'hoch') {
      return {
        ton: 'warn',
        kopf: 'Bei hohem Gewerbeanteil sehen wir nur die halbe Miete.',
        text: 'Die Auswertung deckt den Wohnteil ab. Wie viel das Objekt insgesamt an Miete erzielen kann, '
          + 'hängt dann wesentlich am Gewerbe.',
      };
    }
    return {
      ton: 'info',
      kopf: 'Idealfall.',
      text: 'Für reine Mehrfamilienhäuser liefert die Analyse das beste Ergebnis.',
    };
  }
  if (objektart === 'gewerbe_gebaeude' || objektart === 'gewerbe_einheit') {
    return {
      ton: 'warn',
      kopf: 'Bei Gewerbe gibt es große Unterschiede.',
      text: 'Gewerbemieten folgen ganz anderen Maßstäben als Wohnungsmieten: Branche, '
        + 'Lauflage, Vertragslaufzeit. Ein Experte von uns prüft das Objekt '
        + 'und meldet sich mit einer Einschätzung bei Ihnen.',
    };
  }
  return null;
}
